import { useLayoutEffect, useRef, useState } from "react";

import AnnouncementPageId from "@mapmycustomers/shared/enum/announcements/AnnouncementPageId";

const ARRIVAL_SETTLE_MS = 500;

// Returns whether the URL has stayed put for ARRIVAL_SETTLE_MS since mount or the last page change.
// Calls onArrive once per page-id change once it settles, so a redirect lands first, and onWait when
// a new page id starts that wait.
const useAnnouncementsPageArrival = (
  pageId: AnnouncementPageId | undefined,
  pathname: string,
  onArrive: () => void,
  onWait: () => void
): boolean => {
  const arrivedPageIdRef = useRef(pageId);
  const arrivalPendingRef = useRef(false);
  const settlingRef = useRef(true);
  const [settled, setSettled] = useState(false);

  // A layout effect, so the wait starts before the new page's guides can auto-start.
  useLayoutEffect(() => {
    const isNewPageId = pageId !== arrivedPageIdRef.current;
    if (isNewPageId) {
      arrivedPageIdRef.current = pageId;
      arrivalPendingRef.current = true;
      settlingRef.current = true;
      onWait();
    }
    if (!settlingRef.current) {
      return;
    }
    setSettled(false);
    // A URL change inside the window, a redirect included, cancels this wait and starts another.
    const timer = window.setTimeout(() => {
      settlingRef.current = false;
      setSettled(true);
      if (arrivalPendingRef.current) {
        arrivalPendingRef.current = false;
        onArrive();
      }
    }, ARRIVAL_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [onArrive, onWait, pageId, pathname]);

  return settled;
};

export default useAnnouncementsPageArrival;
